/**
 * MultiStudyConsider Page
 *
 * Main page for considering a patient for multiple studies after an initial review.
 * Shows quick-match results against active protocols and allows batch evaluation.
 *
 * URL: /mhr/multi-study?sourceReviewId={id}&patientId={id}
 *
 * Features:
 * - Protocol quick match list with selection
 * - Selection summary panel with cost estimates
 * - Batch evaluation with progress tracking
 * - Results summary with links to full reviews
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  AlertCircle,
  RefreshCw,
  Loader2,
  CheckCircle,
  X,
  Users,
  DollarSign,
  ListChecks,
  Play,
  BellOff,
  ChevronRight,
  FileText,
} from 'lucide-react';

// Components
import { ProtocolQuickMatchCard } from '../components/MultiStudy/ProtocolQuickMatchCard';
import { EvaluationProgress, ProtocolProgress } from '../components/MultiStudy/EvaluationProgress';
import { CostSavingsIndicator } from '../components/MultiStudy/CostSavingsIndicator';
import { Toast } from '../components/Toast';

// API and types
import {
  quickMatchProtocols,
  startMultiStudyEvaluation,
  getMultiStudyEvaluation,
} from '../lib/api';
import type { QuickMatchResult, MultiStudyEvaluation, MatchLevel } from '../types';

// =====================================================
// Helper Components
// =====================================================

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header skeleton */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="flex-1">
          <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
          <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>

      {/* Card grid skeleton */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-48 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
  onBack,
}: {
  error: string;
  onRetry: () => void;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="text-center max-w-md">
        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Unable to Load Protocols</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px] sm:min-h-0"
          >
            <ArrowLeft size={18} />
            Back to Review
          </button>
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] sm:min-h-0"
          >
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="text-center max-w-md">
        <Users className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No Other Protocols Available</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          There are no other active protocols to evaluate this patient against at this time.
        </p>
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px] sm:min-h-0"
        >
          <ArrowLeft size={18} />
          Back to Review
        </button>
      </div>
    </div>
  );
}

// =====================================================
// Selection Summary Panel Component
// =====================================================

interface SelectionSummaryProps {
  selectedProtocols: QuickMatchResult[];
  onStartEvaluation: () => void;
  isStarting: boolean;
}

function SelectionSummary({ selectedProtocols, onStartEvaluation, isStarting }: SelectionSummaryProps) {
  const totalCost = useMemo(
    () => selectedProtocols.reduce((sum, p) => sum + p.estimated_cost, 0),
    [selectedProtocols]
  );

  const totalNewCriteria = useMemo(
    () => selectedProtocols.reduce((sum, p) => sum + p.new_analysis_criteria_count, 0),
    [selectedProtocols]
  );

  const totalReusable = useMemo(
    () => selectedProtocols.reduce((sum, p) => sum + p.reusable_criteria_count, 0),
    [selectedProtocols]
  );

  if (selectedProtocols.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <ListChecks className="w-8 h-8 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
          <p className="text-sm">Select protocols to evaluate</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-primary-200 dark:border-primary-800 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-100 dark:border-primary-800">
        <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-300">Selection Summary</h3>
      </div>

      {/* Stats */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Protocols selected</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selectedProtocols.length}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Criteria to analyze</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{totalNewCriteria}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Criteria reusable</span>
          <span className="text-sm font-semibold text-green-600 dark:text-green-400">{totalReusable}</span>
        </div>
        <div className="pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Estimated total cost</span>
          <span className="text-base font-bold text-primary-700 dark:text-primary-400">${totalCost.toFixed(2)}</span>
        </div>
      </div>

      {/* Action button */}
      <div className="px-4 pb-4">
        <button
          onClick={onStartEvaluation}
          disabled={isStarting}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {isStarting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <Play size={18} />
              Start Evaluation
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// =====================================================
// Results Summary Component
// =====================================================

interface ResultsSummaryProps {
  evaluation: MultiStudyEvaluation;
  onClose: () => void;
}

function ResultsSummary({ evaluation, onClose }: ResultsSummaryProps) {
  const completedProtocols = evaluation.per_protocol_outcomes.filter(
    (p) => p.status === 'completed'
  );
  const failedProtocols = evaluation.per_protocol_outcomes.filter(
    (p) => p.status === 'failed'
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Evaluation Complete</h3>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Protocol results list */}
      <div className="p-4 space-y-3">
        {completedProtocols.map((outcome) => (
          <Link
            key={outcome.protocol_id}
            to={outcome.result?.review_id ? `/mhr/reviews/${outcome.result.review_id}` : '#'}
            className="block p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{outcome.protocol_name}</span>
              </div>
              <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
            </div>
            {outcome.result && (
              <p className="text-xs text-green-700 dark:text-green-300 mt-1 ml-6">
                {outcome.result.overall_result === 'eligible'
                  ? 'Likely Eligible'
                  : outcome.result.overall_result === 'not_eligible'
                    ? 'Not Eligible'
                    : 'Review Needed'}
              </p>
            )}
          </Link>
        ))}

        {failedProtocols.map((outcome) => (
          <div
            key={outcome.protocol_id}
            className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{outcome.protocol_name}</span>
            </div>
            {outcome.error && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1 ml-6">{outcome.error}</p>
            )}
          </div>
        ))}
      </div>

      {/* Cost summary */}
      <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">Total cost</span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">${evaluation.actual_cost.toFixed(2)}</span>
        </div>
        {evaluation.cost_saved > 0 && (
          <div className="flex items-center justify-between text-sm mt-1">
            <span className="text-gray-600 dark:text-gray-400">Saved from reuse</span>
            <span className="font-semibold text-green-600 dark:text-green-400">${evaluation.cost_saved.toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================
// Main Component
// =====================================================

export function MultiStudyConsider() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract query params
  const sourceReviewId = searchParams.get('sourceReviewId');
  const patientId = searchParams.get('patientId');

  // State
  const [quickMatchResults, setQuickMatchResults] = useState<QuickMatchResult[]>([]);
  const [selectedProtocolIds, setSelectedProtocolIds] = useState<string[]>([]);
  const [evaluationId, setEvaluationId] = useState<string | null>(null);
  const [evaluationStatus, setEvaluationStatus] = useState<MultiStudyEvaluation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dismissedEvaluation, setDismissedEvaluation] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const clearSuccess = useCallback(() => setSuccess(null), []);

  // Polling ref
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Validate required params
  useEffect(() => {
    if (!sourceReviewId || !patientId) {
      setError('Missing required parameters. Please navigate from a completed review.');
      setIsLoading(false);
    }
  }, [sourceReviewId, patientId]);

  // Load quick match results
  const loadQuickMatch = useCallback(async () => {
    if (!sourceReviewId || !patientId) return;

    setIsLoading(true);
    setError(null);

    try {
      const results = await quickMatchProtocols(patientId, sourceReviewId);
      // Sort by match level priority
      const sortOrder: Record<MatchLevel, number> = {
        likely_eligible: 0,
        review_needed: 1,
        likely_excluded: 2,
        no_data: 3,
      };
      const sorted = results.sort(
        (a, b) => sortOrder[a.match_level] - sortOrder[b.match_level]
      );
      setQuickMatchResults(sorted);
    } catch (err: any) {
      console.error('[MultiStudyConsider] Error loading quick match:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load protocols');
    } finally {
      setIsLoading(false);
    }
  }, [sourceReviewId, patientId]);

  // Initial load
  useEffect(() => {
    loadQuickMatch();
  }, [loadQuickMatch]);

  // Handle protocol selection toggle
  const handleToggleSelect = useCallback((protocolId: string) => {
    setSelectedProtocolIds((prev) =>
      prev.includes(protocolId)
        ? prev.filter((id) => id !== protocolId)
        : [...prev, protocolId]
    );
  }, []);

  // Get selected protocols
  const selectedProtocols = useMemo(
    () => quickMatchResults.filter((p) => selectedProtocolIds.includes(p.protocol_id)),
    [quickMatchResults, selectedProtocolIds]
  );

  // Filter protocols by search
  const filteredProtocols = useMemo(() => {
    if (!searchQuery.trim()) return quickMatchResults;
    const query = searchQuery.toLowerCase();
    return quickMatchResults.filter((p) =>
      p.protocol_name.toLowerCase().includes(query)
    );
  }, [quickMatchResults, searchQuery]);

  // Start evaluation
  const handleStartEvaluation = useCallback(async () => {
    if (!sourceReviewId || !patientId || selectedProtocolIds.length === 0) return;

    setIsStarting(true);
    setError(null);

    try {
      const result = await startMultiStudyEvaluation(
        patientId,
        sourceReviewId,
        selectedProtocolIds
      );
      setEvaluationId(result.evaluation_id);
      setDismissedEvaluation(false);
      setSuccess(`Evaluation started for ${selectedProtocolIds.length} protocol${selectedProtocolIds.length > 1 ? 's' : ''}`);
    } catch (err: any) {
      console.error('[MultiStudyConsider] Error starting evaluation:', err);
      setError(err.response?.data?.error || err.message || 'Failed to start evaluation');
    } finally {
      setIsStarting(false);
    }
  }, [sourceReviewId, patientId, selectedProtocolIds]);

  // Poll for evaluation status
  useEffect(() => {
    if (!evaluationId) return;

    const pollStatus = async () => {
      try {
        const status = await getMultiStudyEvaluation(evaluationId);
        setEvaluationStatus(status);

        // Stop polling if complete or failed
        if (status.status === 'completed' || status.status === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } catch (err: any) {
        console.error('[MultiStudyConsider] Error polling status:', err);
      }
    };

    // Initial fetch
    pollStatus();

    // Set up polling interval (every 2.5 seconds)
    pollingIntervalRef.current = setInterval(pollStatus, 2500);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [evaluationId]);

  // Handle continue in background
  const handleContinueInBackground = useCallback(() => {
    setDismissedEvaluation(true);
    // Could show a toast notification here about checking back later
  }, []);

  // Handle close results
  const handleCloseResults = useCallback(() => {
    setEvaluationId(null);
    setEvaluationStatus(null);
    setSelectedProtocolIds([]);
    loadQuickMatch(); // Refresh the list
  }, [loadQuickMatch]);

  // Navigate back to source review
  const handleBack = useCallback(() => {
    if (sourceReviewId) {
      navigate(`/mhr/reviews/${sourceReviewId}`);
    } else {
      navigate('/mhr');
    }
  }, [navigate, sourceReviewId]);

  // Convert evaluation status to progress format
  const evaluationProgress = useMemo((): ProtocolProgress[] => {
    if (!evaluationStatus) return [];
    return evaluationStatus.per_protocol_outcomes.map((o) => ({
      protocolId: o.protocol_id,
      protocolName: o.protocol_name,
      status: o.status,
      error: o.error,
    }));
  }, [evaluationStatus]);

  const overallProgress = useMemo(() => {
    if (!evaluationStatus) return 0;
    const total = evaluationStatus.per_protocol_outcomes.length;
    const done = evaluationStatus.per_protocol_outcomes.filter(
      (p) => p.status === 'completed' || p.status === 'failed'
    ).length;
    return total > 0 ? Math.round((done / total) * 100) : 0;
  }, [evaluationStatus]);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton />
      </div>
    );
  }

  // Error state (missing params or API error)
  if (error && !quickMatchResults.length) {
    return (
      <ErrorState
        error={error}
        onRetry={loadQuickMatch}
        onBack={handleBack}
      />
    );
  }

  // Empty state (no protocols available)
  if (!quickMatchResults.length) {
    return <EmptyState onBack={handleBack} />;
  }

  // Check if evaluation is in progress and not dismissed
  const showEvaluationUI = evaluationId && !dismissedEvaluation;
  const isEvaluationComplete = evaluationStatus?.status === 'completed' || evaluationStatus?.status === 'failed';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to={sourceReviewId ? `/mhr/reviews/${sourceReviewId}` : '/mhr'}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Consider for Other Studies</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Patient {patientId?.slice(0, 8)}...
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadQuickMatch}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner (if error but has results) */}
      {error && quickMatchResults.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            aria-label="Dismiss error"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Main content */}
      {showEvaluationUI ? (
        /* Evaluation In Progress / Results View */
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Progress / Results panel */}
          <div className="lg:col-span-2 space-y-4">
            {isEvaluationComplete && evaluationStatus ? (
              <ResultsSummary
                evaluation={evaluationStatus}
                onClose={handleCloseResults}
              />
            ) : (
              <>
                <EvaluationProgress
                  evaluationId={evaluationId!}
                  protocols={evaluationProgress}
                  overallProgress={overallProgress}
                />

                {/* Continue in background button */}
                {!isEvaluationComplete && (
                  <div className="flex justify-center">
                    <button
                      onClick={handleContinueInBackground}
                      className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
                    >
                      <BellOff size={16} />
                      Continue in Background
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Cost savings panel */}
          <div>
            {evaluationStatus && (
              <CostSavingsIndicator
                criteriaReused={evaluationStatus.criteria_reused_count}
                criteriaAnalyzed={evaluationStatus.criteria_analyzed_count}
                estimatedCost={selectedProtocols.reduce((sum, p) => sum + p.estimated_cost, 0)}
                actualCost={isEvaluationComplete ? evaluationStatus.actual_cost : undefined}
                costSaved={evaluationStatus.cost_saved}
              />
            )}
          </div>
        </div>
      ) : (
        /* Protocol Selection View */
        <>
          {/* Search and count */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search protocols..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {quickMatchResults.length} protocol{quickMatchResults.length !== 1 ? 's' : ''} available
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Protocol cards grid */}
            <div className="lg:col-span-2">
              {filteredProtocols.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700">
                  <FileText className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No protocols match your search</h3>
                  <p className="text-gray-600 dark:text-gray-400">Try adjusting your search terms</p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {filteredProtocols.map((result) => (
                    <ProtocolQuickMatchCard
                      key={result.protocol_id}
                      protocolId={result.protocol_id}
                      protocolName={result.protocol_name}
                      matchLevel={result.match_level}
                      reusableCriteriaCount={result.reusable_criteria_count}
                      newAnalysisCriteriaCount={result.new_analysis_criteria_count}
                      totalCriteriaCount={result.total_criteria_count}
                      estimatedCost={result.estimated_cost}
                      selected={selectedProtocolIds.includes(result.protocol_id)}
                      onToggleSelect={() => handleToggleSelect(result.protocol_id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Selection summary panel (sticky on desktop) */}
            <div className="lg:sticky lg:top-6 lg:self-start">
              <SelectionSummary
                selectedProtocols={selectedProtocols}
                onStartEvaluation={handleStartEvaluation}
                isStarting={isStarting}
              />
            </div>
          </div>
        </>
      )}

      {/* Background evaluation indicator (shows when dismissed but still polling) */}
      {dismissedEvaluation && evaluationId && !isEvaluationComplete && (
        <div className="fixed bottom-4 right-4 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-sm">Evaluation running...</span>
          <button
            onClick={() => setDismissedEvaluation(false)}
            className="ml-2 text-primary-200 hover:text-white"
          >
            View
          </button>
        </div>
      )}

      {/* Success Toast */}
      <Toast message={success} type="success" onClose={clearSuccess} />
    </div>
  );
}

export default MultiStudyConsider;
